import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import _ from 'lodash'
import { Box, Divider, Typography } from '@material-ui/core'
import { programViewStyles } from './style'
import { AreaSeries, ChartLabel, Crosshair, FlexibleXYPlot, GradientDefs, Hint, HorizontalGridLines, LabelSeries, VerticalBarSeries, XAxis, YAxis } from 'react-vis'
import moment from 'moment'

const axisStyle = {
  ticks: {
    fontSize: '10px',
    fill: '#a1aab3',
  },
  line: {
    stroke: '#a1aab3',
  },
  title: {
    fontSize: '10px',
    fill: '#7b8793'
  },
}

function BatteryChargeGraph(props) {
  const classes = programViewStyles()

  const [hintValue, setHintValue] = useState()
  const [maxYValue, setMaxYValue] = useState(10)
  const [data, setData] = useState([])
  const [energyAreaData, setEnergyAreaData] = useState([])
  const [xAxis, setXAxis] = useState([])

  let startInserted = [], endInserted = []

  const { selectedValue } = props
  const { programs_id: programId } = useParams()
  const { currentUser, timeZone } = useSelector(state => state.auth)

  const getData = (selectedDateRange, graphData, eventsData) => {
    const start = moment().tz(timeZone)
    let remainder = 0
    if (start.minute() % 15 && moment(selectedDateRange.startDate).minutes() % 15) {
      remainder = 15 - (start.minute() % 15)
    }
    let time = moment(selectedDateRange.startDate).add(remainder, 'minutes')
    let utcTime = moment.tz(selectedDateRange.startDate.toLocaleString('en-US'), 'M/D/YYYY, h:mm:ss A', timeZone).add(remainder, 'minutes').utc()
    let modifiedData = []
    graphData = _(graphData).toPairs().sortBy((data) => moment(data[0], 'DD-MM-YYYY').format('YYYYMMDD')).fromPairs().value()
    Object.keys(graphData).forEach(data => {
      modifiedData = [...modifiedData, ...(graphData[data].available_energy_wh ? graphData[data].available_energy_wh : [])]
    })
    modifiedData = modifiedData.map(data => (
      (data / 1000).toFixed(3)
    ))
    setMaxYValue(Math.max(10, Math.max(...modifiedData)))
    let startIndex = (utcTime.hours() * 4 + utcTime.minutes() / 15) - 1
    let data = [], areaGraph = [], xAxis = []
    while (moment(selectedDateRange.endDate).endOf('day').diff(time, 'seconds') > 0 && moment().tz(timeZone).diff(moment.tz(time.format('DD/MM/YYYY HH:mm'), 'DD/MM/YYYY HH:mm', timeZone)) > 0) {
      eventsData.forEach(event => {
        if (event.status !== 'cancelled') {
          let energyAreaData = {}
          let startDate = moment.utc(event.start_date_time).tz(timeZone).format('DD/MM/YYYY, HH:mm:ss')
          let endDate = moment.utc(event.end_date_time).tz(timeZone).format('DD/MM/YYYY, HH:mm:ss')
          if (moment(startDate.toLocaleString(), 'DD/MM/YYYY, HH:mm:ss').tz(timeZone).unix() > moment(time).unix() && !startInserted.includes(event.event_id)) {
            if (!energyAreaData[event.event_id]) {
              energyAreaData[event.event_id] = []
              energyAreaData[event.event_id].push({ x: moment(startDate.toLocaleString(), 'DD/MM/YYYY, HH:mm:ss').unix(), y: maxYValue, label: `Event (Name: ${event.name ? event.name : '-'})`, yOffset: -30, xOffset: -50 })
              energyAreaData[event.event_id].push({ x: moment(startDate.toLocaleString(), 'DD/MM/YYYY, HH:mm:ss').unix(), y: maxYValue, label: `Started`, yOffset: -20, xOffset: -50 })
            }
            if (energyAreaData[event.event_id]) {
              energyAreaData[event.event_id].push({ x: moment(endDate.toLocaleString(), 'DD/MM/YYYY, HH:mm:ss').unix(), y: maxYValue, label: `Event (Name: ${event.name ? event.name : '-'})`, yOffset: -10, xOffset: 10 })
              energyAreaData[event.event_id].push({ x: moment(endDate.toLocaleString(), 'DD/MM/YYYY, HH:mm:ss').unix(), y: maxYValue, label: `Ended`, yOffset: 0, xOffset: 10 })
            }
            data.push({ x: moment(startDate.toLocaleString(), 'DD/MM/YYYY, HH:mm:ss').unix(), y: null })
            data.push({ x: moment(endDate.toLocaleString(), 'DD/MM/YYYY, HH:mm:ss').unix(), y: null })
            startInserted.push(event.event_id)
          }
          if (Object.keys(energyAreaData).length > 0)
            areaGraph.push(energyAreaData)
        }
      })
      xAxis.push(moment(time).unix())
      data.push({ x: moment(time).unix(), y: modifiedData[startIndex] ? modifiedData[startIndex] : 0 })
      startIndex++
      time = moment(time).add(15, 'minutes')
      utcTime = moment(utcTime).add(15, 'minutes')
    }
    setXAxis(xAxis)
    setData(data)
    props.setEnergyData(data)
    setEnergyAreaData(areaGraph)
  }

  useEffect(() => {
    getData(props.selectedDateRange, props.graphData, props.eventsData)
  }, [props.selectedDateRange, props.graphData, props.eventsData])

  return (
    <>
      <Box m={3} className={classes.graphWrapper}>
        <FlexibleXYPlot
          height={300}
          xType="linear"
          yDomain={[0, Math.max(maxYValue + 2, Math.floor(maxYValue * 1.1))]}
          margin={{ bottom: 50, right: 20, left: 60 }}
          onMouseLeave={() => { props.setCrossHairValues([]) }}
        >
          <GradientDefs>
            <linearGradient id="EnergyGradient" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#D7E5F5" stopOpacity={1} />
            </linearGradient>
          </GradientDefs>
          <HorizontalGridLines />
          <VerticalBarSeries
            color="#387AD5"
            onNearestX={(value, { event, innerX, index }) => {
              let events = []
              props.eventsData.forEach(event => {
                let startDate = moment.utc(event.start_date_time).tz(timeZone).format('DD/MM/YYYY, HH:mm:ss')
                let endDate = moment.utc(event.end_date_time).tz(timeZone).format('DD/MM/YYYY, HH:mm:ss')
                if (moment(startDate.toLocaleString(), 'DD/MM/YYYY, HH:mm:ss').tz(timeZone).unix() <= data[index].x && moment(endDate.toLocaleString(), 'DD/MM/YYYY, HH:mm:ss').tz(timeZone).unix() >= data[index].x && event.status !== 'cancelled') {
                  events.push(event)
                }
              })
              props.setCrossHairValues([data[index], props.powerData[index], { events: events }])
            }}
            data={data}
            barWidth={data.length > 150 ? '0.1' : '0.5'}
            style={{ position: 'relative' }}
          />
          {energyAreaData.map(dataset => (
            Object.keys(dataset).map(key => (
              <AreaSeries
                opacity={0.5}
                color={'url(#EnergyGradient)'}
                yType="linear"
                data={dataset[key]}
              />
            ))
          )
          )}
          <XAxis
            top={0}
            hideLine
            style={{ ...axisStyle, line: { display: 'none' } }}
            tickValues={xAxis}
            tickFormat={(t, i) => {
              if (moment.unix(t).format('h:mm A') === '12:00 PM' && i > 10) {
                let tick = moment.unix(t).format('DD/MM/YYYY h:mm A')
                return <tspan><tspan x="0" dy="1em">{`${tick.split(' ')[0]}`}</tspan></tspan>
              } else {
                return
              }
            }} />
          <XAxis
            style={axisStyle}
            tickLabelAngle={window.innerWidth <= 900 ? -45 : 0}
            tickValues={xAxis}
            tickFormat={(t, i) => {
              if ((i + 1) % Math.floor(data.length / 13) === 0) {
                let tick = moment.unix(t).format('DD/MM/YYYY h:mm A')
                return <tspan><tspan x="0" dy="1em">{`${tick.split(' ')[1]}`}</tspan><tspan x="0" dy="1em">{`${tick.split(' ')[2]}`}</tspan></tspan>
              } else {
                return
              }
            }}
          />
          <YAxis style={axisStyle} />
          <ChartLabel
            text="Energy (kWh)"
            includeMargin={true}
            style={{
              transform: 'rotate(-90)',
              textAnchor: 'end',
              color: '#a1aab3',
              fontSize: '12px',
            }}
          />
          <Crosshair values={props.crossHairValues} className={`${classes.crossHair} ${classes.hint}`}>
            <Box className={`${classes.hintCard} custom-hintbox`}>
              <Box>
                {props.crossHairValues[0] && props.crossHairValues[0].y &&
                  <>
                    <Box className={`${classes.hintTextWrapper}`}>
                      <Typography className={classes.cardKey}>Time:</Typography>
                      <Typography className={classes.cardValue}>{moment.unix(props.crossHairValues[0]?.x).format('DD/MM/YYYY h:mm A')}</Typography>
                    </Box>
                    <Box className={`${classes.hintTextWrapper}`}>
                      <Typography className={classes.cardKey}>Energy:</Typography>
                      <Typography className={classes.cardValue}>{props.crossHairValues[0]?.y}kWh</Typography>
                    </Box>
                  </>
                }
                {props.crossHairValues[2]?.events?.length > 0 &&
                  <Box>
                    {props.crossHairValues[2]?.events.map((event, index) => (
                      <>
                        {(props.crossHairValues[0] && props.crossHairValues[0].y || event && index !== 0) &&
                          <Box my={1}>
                            <Divider />
                          </Box>
                        }
                        <Box className={classes.cardEvent}>{`Event Name : ${event.name}`}</Box>
                        <Typography className={classes.cardEvent}>{`Started at : ${moment.utc(event.start_date_time).tz(timeZone).format('h:mm A')}`}</Typography>
                        <Typography className={classes.cardEvent}>{`Ended at : ${moment.utc(event.end_date_time).tz(timeZone).format('h:mm A')}`}</Typography>
                      </>
                    ))}
                  </Box>
                }
              </Box>
            </Box>
          </Crosshair>
        </FlexibleXYPlot>
      </Box>
    </>
  )
}

export default BatteryChargeGraph